import { useRef, useState, lazy, Suspense } from 'react';
import BrandMark from './BrandMark';
import DebugPinModal from './DebugPinModal';

// Admin cards are heavy (large tables, lots of state). Lazy-load them
// so the header keeps its zero-cost footprint on every screen; the
// bundle is only fetched when someone actually taps the logo 10×.
const AdminUsersCard = lazy(() => import('./AdminUsersCard'));
const AdminPlansCard = lazy(() => import('./AdminPlansCard'));
const AdminSignupFunnelCard = lazy(() => import('./AdminSignupFunnelCard'));
const AdminTestimonialsCard = lazy(() => import('./AdminTestimonialsCard'));
const AdminSocialLinksCard = lazy(() => import('./AdminSocialLinksCard'));
const AdminEmailDiagCard = lazy(() => import('./AdminEmailDiagCard'));
const AdminStarterLibraryCard = lazy(() => import('./AdminStarterLibraryCard'));
const AdminChangePinCard = lazy(() => import('./AdminChangePinCard'));
const ModerationFlagsCard = lazy(() => import('./ModerationFlagsCard'));

/**
 * HeaderDebugGate — iter-51.15.
 *
 * The 10-tap secret gesture used to live only on the Reading page
 * (see `pages/Reading.jsx :: registerSecretTap`), which meant a fresh
 * account with zero books couldn't reach the admin panel at all —
 * the only screen with the gesture was locked behind having a book.
 *
 * This component wraps the app-wide header BrandMark with an
 * `onClick` (fires for BOTH touch taps AND mouse clicks — user
 * explicitly asked for laptop support) that runs the same 10-tap-
 * in-4-seconds unlock, prompts for the debug PIN, and renders a
 * full-screen admin drawer over whatever page the user was on. The
 * Reading-page's inline scoring debug stays as-is; this is a
 * separate, screen-agnostic entry point.
 */
export default function HeaderDebugGate() {
  const [pinAsking, setPinAsking] = useState(false);
  const [pin, setPin] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [progress, setProgress] = useState(0); // 5-9 → visible dot
  const tapsRef = useRef({ count: 0, first: 0 });

  const onLogoTap = () => {
    // If the drawer is already open, a tap on the logo should NOT
    // start another unlock attempt — otherwise a closing tap could
    // race the counter.
    if (drawerOpen || pinAsking) return;
    const now = Date.now();
    const s = tapsRef.current;
    // 4 s window, same as the Reading-page gesture — accidental
    // triggers from a scrolling child are essentially impossible.
    if (now - s.first > 4000) { s.count = 0; s.first = now; }
    s.count += 1;
    setProgress(s.count >= 5 && s.count < 10 ? s.count : 0);
    if (s.count >= 10) {
      s.count = 0; s.first = 0; setProgress(0);
      setPinAsking(true);
    }
  };

  return (
    <>
      {/*
        `role="button"` + `tabIndex={0}` makes the mark clickable via
        keyboard too (Enter/Space), useful on a laptop without a mouse.
        `onClick` alone would already fire for BOTH touch and mouse —
        the user was explicit about needing this on laptop, so we're
        deliberately NOT using pointerdown/touchstart.
      */}
      <div data-testid="header-debug-tap-target"
        role="button" tabIndex={0}
        onClick={onLogoTap}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onLogoTap(); }}
        className="cursor-pointer select-none relative"
        aria-label="ReadBuddy">
        <BrandMark size="md"/>
        {progress > 0 && (
          <div data-testid="header-debug-tap-progress"
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i}
                className={`w-1 h-1 rounded-full ${i < progress - 4 ? 'bg-[#FF6B1A]' : 'bg-transparent'}`}/>
            ))}
          </div>
        )}
      </div>

      {pinAsking && (
        <DebugPinModal
          onUnlocked={(verifiedPin) => { setPinAsking(false); setPin(verifiedPin || ''); setDrawerOpen(true); }}
          onCancel={() => setPinAsking(false)}
        />
      )}

      {drawerOpen && (
        <div data-testid="header-admin-drawer"
          className="fixed inset-0 z-40 bg-slate-100/95 backdrop-blur-sm overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-5xl mx-auto p-4 pb-40"
            style={{ paddingBottom: 'max(10rem, env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-slate-900 font-bold uppercase tracking-wider text-sm">
                Admin panel
              </div>
              <button data-testid="header-admin-drawer-close"
                onClick={() => { setDrawerOpen(false); setPin(''); }}
                className="w-9 h-9 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-800 text-xl">
                ×
              </button>
            </div>
            <Suspense fallback={
              <div className="text-slate-700 text-sm py-8 text-center">Loading admin panel…</div>
            }>
              <div className="space-y-3 text-[13px] text-slate-800">
                <AdminChangePinCard debugPin={pin} onChanged={(np) => setPin(np)}/>
                <AdminSignupFunnelCard debugPin={pin}/>
                <AdminUsersCard debugPin={pin}/>
                <AdminPlansCard debugPin={pin}/>
                <AdminTestimonialsCard debugPin={pin}/>
                <AdminSocialLinksCard debugPin={pin}/>
                <AdminEmailDiagCard debugPin={pin}/>
                <AdminStarterLibraryCard debugPin={pin}/>
                <ModerationFlagsCard debugPin={pin}/>
              </div>
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
}
